import type { ImmerStateCreator } from 'hooks/useStore';
import type { ReportingEngineTicketingReportingWidgetDefinition } from 'containers/ReportingEngine/_api/types';
import { getDefinition } from 'containers/ReportingEngine/helpers/definitionAdapter';
import type { APIVisualization } from 'containers/ReportingEngine/_api/types/api_visualization';
import { getChartVisualization } from 'containers/ReportingEngine/helpers/visualizationAdapter';
import type { LayoutItem } from 'react-grid-layout/legacy';
import type {
  DatatableField,
  DatatableSort,
  FilterField,
  ChartDisplay,
  ChartXAxis,
  ChartYAxis,
} from '../Widgets/types';
import {
  BarChartDisplay,
  ChartTypes,
  ChartDisplayOption,
  ChartFormula,
  ChartSort,
  TabOptions,
  MetricFormattingDisplay,
  EntitySourceOperator,
} from '../Widgets/types';

export interface ReportingEngineWidgetSlice {
  hasUnsavedChanges: boolean;
  widgetName: string;
  entitySource: string[];
  definition: ReportingEngineTicketingReportingWidgetDefinition | null;
  selectedWidgetId: string | null;
  isShowCreateModal: boolean;
  selectedTab: TabOptions;
  allFields: DatatableField[];
  datatableFields: DatatableField[];
  datatableSort: DatatableSort | null;
  filters: FilterField[];
  satisfyAll: boolean;
  selectedChartType: ChartTypes;
  chartXAxis: ChartXAxis[];
  chartYAxis: ChartYAxis;
  chartDisplay: ChartDisplay;
  barChartDisplay: BarChartDisplay;
  metricFormattingDisplay: MetricFormattingDisplay;
  metricDecimalsDisplay: string;
  metricShowThousandSeparator: boolean;
  metricCurrencySymbol: string;
  visualization: APIVisualization | null;
  isShowCreateDashboardModal: boolean;
  isShowEditDashboardModal: boolean;
  selectedDashboard: {
    label: string;
    value: string;
    description: string;
  } | null;
  maxDashboardHeight: number;
  movingItem: LayoutItem | null;
  hasScrollbar: boolean;
  isSelectAllBoard: boolean;
  entitySourceOperator: EntitySourceOperator;
  studioError: string | null;
  studioErrorDatatable: string | null;
  isModalOpen: boolean;
  setIsModalOpen: (isModalOpen: boolean) => void;
  setHasScrollbar: (hasScrollbar: boolean) => void;
  setMovingItem: (item: LayoutItem | null) => void;
  setMaxDashboardHeight: (height: number) => void;
  setIsShowEditDashboardModal: (isShow: boolean) => void;
  setHasUnsavedChanges: (isHasUnsavedChanges: boolean) => void;
  setEntitySource: (boards: string[]) => void;
  setWidgetName: (name: string) => void;
  setAllFields: (fields: DatatableField[]) => void;
  setSelectedWidgetId: (widgetId: string | null) => void;
  showCreateModal: (isShow: boolean) => void;
  setSelectedTab: (tab: TabOptions) => void;
  setDatatableFields: (fields: DatatableField[]) => void;
  setDatatableSort: (sort: DatatableSort | null) => void;
  setFilters: (filters: FilterField[]) => void;
  setSatisfyAll: (satisfyAll: boolean) => void;
  setSelectedChartType: (chartType: ChartTypes) => void;
  setChartXAxis: (xAxis: ChartXAxis[]) => void;
  setChartYAxis: (xAxis: ChartYAxis) => void;
  setChartDisplay: (display: ChartDisplay) => void;
  setShowCreateDashboardModal: (isShow: boolean) => void;
  setSelectedDashboard: (
    dashboard: { label: string; value: string; description: string } | null
  ) => void;
  refreshVisualization: () => void;
  setBarChartDisplay: (display: BarChartDisplay) => void;
  setMetricFormattingDisplay: (display: MetricFormattingDisplay) => void;
  setIsSelectAllBoard: (isSelectAll: boolean) => void;
  setEntitySourceOperator: (operator: EntitySourceOperator) => void;
  setStudioError: (error: string | null) => void;
  setStudioErrorDatatable: (error: string | null) => void;
  setMetricDecimalsDisplay: (decimals: string) => void;
  setMetricShowThousandSeparator: (isShowThousandSeparator: boolean) => void;
  setMetricCurrencySymbol: (symbol: string) => void;
}

export const useReportingEngineWidgetSlice: ImmerStateCreator<
  ReportingEngineWidgetSlice
> = (set) => ({
  isShowEditDashboardModal: false,
  selectedDashboard: null,
  isShowCreateDashboardModal: false,
  hasUnsavedChanges: false,
  widgetName: '',
  entitySource: ['all'],
  definition: null,
  selectedWidgetId: null,
  isShowCreateModal: false,
  selectedTab: TabOptions.DataTable,
  allFields: [],
  datatableFields: [],
  datatableSort: null,
  filters: [],
  satisfyAll: true,
  selectedChartType: ChartTypes.LIST,
  chartXAxis: [],
  chartYAxis: {
    formula: ChartFormula.COUNT,
    measure: ChartFormula.COUNT,
  },
  chartDisplay: {
    sort: ChartSort.X_Axis,
    order: 'asc',
    displayOption: ChartDisplayOption.ALL,
  },
  visualization: null,
  maxDashboardHeight: 0,
  movingItem: null,
  hasScrollbar: false,
  isSelectAllBoard: true,
  entitySourceOperator: EntitySourceOperator.INCLUDES_ANY_OF,
  studioError: null,
  studioErrorDatatable: null,
  metricShowThousandSeparator: false,
  isModalOpen: false,
  setIsModalOpen: (isModalOpen: boolean) => {
    set(
      (state) => {
        state.reportingEngineWidgetSlice.isModalOpen = isModalOpen;
      },
      false,
      'setIsModalOpen'
    );
  },
  setHasScrollbar: (hasScrollbar: boolean) =>
    set(
      (state) => {
        state.reportingEngineWidgetSlice.hasScrollbar = hasScrollbar;
      },
      false,
      'setHasScrollbar'
    ),
  setMovingItem: (item: LayoutItem | null) =>
    set(
      (state) => {
        state.reportingEngineWidgetSlice.movingItem = item;
      },
      false,
      'setMovingItem'
    ),
  setMaxDashboardHeight: (height: number) =>
    set(
      (state) => {
        state.reportingEngineWidgetSlice.maxDashboardHeight = height;
      },
      false,
      'setMaxDashboardHeight'
    ),
  barChartDisplay: BarChartDisplay.SIDE,
  metricFormattingDisplay: MetricFormattingDisplay.NUMBER,
  metricDecimalsDisplay: '2',
  metricCurrencySymbol: '$',
  setIsShowEditDashboardModal: (isShow: boolean) =>
    set(
      (state) => {
        state.reportingEngineWidgetSlice.isShowEditDashboardModal = isShow;
      },
      false,
      'setIsShowEditDashboardModal'
    ),
  setSelectedDashboard: (
    dashboard: { label: string; value: string; description: string } | null
  ) =>
    set(
      (state) => {
        state.reportingEngineWidgetSlice.selectedDashboard = dashboard;
      },
      false,
      'setSelectedDashboard'
    ),
  setShowCreateDashboardModal: (isShow: boolean) =>
    set(
      (state) => {
        state.reportingEngineWidgetSlice.isShowCreateDashboardModal = isShow;
      },
      false,
      'setShowCreateDashboardModal'
    ),
  setHasUnsavedChanges: (isHasUnsavedChanges: boolean) =>
    set(
      (state) => {
        state.reportingEngineWidgetSlice.hasUnsavedChanges =
          isHasUnsavedChanges;
      },
      false,
      'setHasUnsavedChanges'
    ),
  setEntitySource: (boards: string[]) =>
    set(
      (state) => {
        state.reportingEngineWidgetSlice.entitySource = boards;
      },
      false,
      'setEntitySource'
    ),
  setWidgetName: (name: string) =>
    set(
      (state) => {
        state.reportingEngineWidgetSlice.widgetName = name;
      },
      false,
      'setWidgetName'
    ),
  setAllFields: (fields: DatatableField[]) =>
    set(
      (state) => {
        state.reportingEngineWidgetSlice.allFields = fields;
      },
      false,
      'setAllFields'
    ),
  setSelectedWidgetId: (widgetId: string | null) =>
    set(
      (state) => {
        state.reportingEngineWidgetSlice.selectedWidgetId = widgetId;
      },
      false,
      'setSelectedWidgetId'
    ),
  showCreateModal: (isShow: boolean) =>
    set(
      (state) => {
        state.reportingEngineWidgetSlice.isShowCreateModal = isShow;
      },
      false,
      'showCreateModal'
    ),
  setSelectedTab: (tab: TabOptions) =>
    set(
      (state) => {
        state.reportingEngineWidgetSlice.selectedTab = tab;
      },
      false,
      'setSelectedTab'
    ),
  setDatatableFields: (fields: DatatableField[]) =>
    set(
      (state) => {
        // check if datatableSort is not null and if the field is not in the datatableFields
        if (
          state.reportingEngineWidgetSlice.datatableSort &&
          !fields.find(
            (field) =>
              field.id ===
              state.reportingEngineWidgetSlice.datatableSort?.sortBy
          )
        ) {
          state.reportingEngineWidgetSlice.datatableSort = null;
        }

        // check if the field is not in the lineChartXAxis
        state.reportingEngineWidgetSlice.chartXAxis =
          state.reportingEngineWidgetSlice.chartXAxis.filter((xAxis) =>
            fields.find((field) => field.id === xAxis.id)
          );

        state.reportingEngineWidgetSlice.datatableFields = fields;

        // generate definition
        state.reportingEngineWidgetSlice.definition = getDefinition(
          state.reportingEngineWidgetSlice.allFields,
          fields,
          state.reportingEngineWidgetSlice.datatableSort,
          state.reportingEngineWidgetSlice.filters,
          state.reportingEngineWidgetSlice.satisfyAll
        );

        // regenerate visualization
        state.reportingEngineWidgetSlice.visualization = getChartVisualization(
          state.reportingEngineWidgetSlice
        );
      },
      false,
      'setDatatableFields'
    ),
  setDatatableSort: (sort: DatatableSort | null) =>
    set(
      (state) => {
        state.reportingEngineWidgetSlice.datatableSort = sort;

        // generate definition
        state.reportingEngineWidgetSlice.definition = getDefinition(
          state.reportingEngineWidgetSlice.allFields,
          state.reportingEngineWidgetSlice.datatableFields,
          sort,
          state.reportingEngineWidgetSlice.filters,
          state.reportingEngineWidgetSlice.satisfyAll
        );
      },
      false,
      'setDatatableSort'
    ),
  setFilters: (filters: FilterField[]) =>
    set(
      (state) => {
        state.reportingEngineWidgetSlice.filters = filters;

        // generate definition
        state.reportingEngineWidgetSlice.definition = getDefinition(
          state.reportingEngineWidgetSlice.allFields,
          state.reportingEngineWidgetSlice.datatableFields,
          state.reportingEngineWidgetSlice.datatableSort,
          filters,
          state.reportingEngineWidgetSlice.satisfyAll
        );
      },
      false,
      'setFilters'
    ),
  setSatisfyAll: (satisfyAll: boolean) =>
    set(
      (state) => {
        state.reportingEngineWidgetSlice.satisfyAll = satisfyAll;

        // generate definition
        state.reportingEngineWidgetSlice.definition = getDefinition(
          state.reportingEngineWidgetSlice.allFields,
          state.reportingEngineWidgetSlice.datatableFields,
          state.reportingEngineWidgetSlice.datatableSort,
          state.reportingEngineWidgetSlice.filters,
          satisfyAll
        );
      },
      false,
      'setSatisfyAll'
    ),
  setSelectedChartType: (chartType: ChartTypes) =>
    set(
      (state) => {
        state.reportingEngineWidgetSlice.selectedChartType = chartType;

        // reset error
        state.reportingEngineWidgetSlice.studioError = null;

        // adjust x axis
        if (
          (chartType === ChartTypes.PIE || chartType === ChartTypes.METRIC) &&
          state.reportingEngineWidgetSlice.chartXAxis.length > 1
        ) {
          // remove all x axis except the first one
          state.reportingEngineWidgetSlice.chartXAxis = [
            state.reportingEngineWidgetSlice.chartXAxis[0],
          ];
        }

        // generate visualization
        state.reportingEngineWidgetSlice.visualization = getChartVisualization(
          state.reportingEngineWidgetSlice
        );
      },
      false,
      'setSelectedChartType'
    ),
  setChartXAxis: (xAxis: ChartXAxis[]) =>
    set(
      (state) => {
        // check if selected y axis is not in the new x axis
        if (
          state.reportingEngineWidgetSlice.chartYAxis &&
          !xAxis.find(
            ({ id }) =>
              id === state.reportingEngineWidgetSlice.chartYAxis?.measure
          )
        ) {
          state.reportingEngineWidgetSlice.chartYAxis = {
            formula: ChartFormula.COUNT,
            measure: ChartFormula.COUNT,
          };
        }

        state.reportingEngineWidgetSlice.chartXAxis = xAxis;

        // generate visualization
        state.reportingEngineWidgetSlice.visualization = getChartVisualization(
          state.reportingEngineWidgetSlice
        );
      },
      false,
      'setChartXAxis'
    ),
  setChartYAxis: (yAxis: ChartYAxis) =>
    set(
      (state) => {
        // check if measure is not COUNT and dont have formula and not metric type
        if (
          yAxis.formula === ChartFormula.COUNT &&
          yAxis.measure !== ChartFormula.COUNT &&
          state.reportingEngineWidgetSlice.selectedChartType !==
            ChartTypes.METRIC
        ) {
          yAxis.formula = ChartFormula.SUM;
        }

        state.reportingEngineWidgetSlice.chartYAxis = yAxis;

        // generate visualization
        state.reportingEngineWidgetSlice.visualization = getChartVisualization(
          state.reportingEngineWidgetSlice
        );
      },
      false,
      'setChartYAxis'
    ),
  setChartDisplay: (display: ChartDisplay) =>
    set(
      (state) => {
        state.reportingEngineWidgetSlice.chartDisplay = display;

        // generate visualization
        state.reportingEngineWidgetSlice.visualization = getChartVisualization(
          state.reportingEngineWidgetSlice
        );
      },
      false,
      'setChartDisplay'
    ),
  // update visualization with the latest datafield properties
  refreshVisualization: () =>
    set(
      (state) => {
        if (
          [
            ChartTypes.LINE,
            ChartTypes.BAR,
            ChartTypes.PIE,
            ChartTypes.METRIC,
          ].includes(state.reportingEngineWidgetSlice.selectedChartType)
        ) {
          state.reportingEngineWidgetSlice.visualization =
            getChartVisualization(state.reportingEngineWidgetSlice);
        }
      },
      false,
      'refreshVisualization'
    ),
  setBarChartDisplay: (display: BarChartDisplay) =>
    set(
      (state) => {
        state.reportingEngineWidgetSlice.barChartDisplay = display;

        // generate visualization
        state.reportingEngineWidgetSlice.visualization = getChartVisualization(
          state.reportingEngineWidgetSlice
        );
      },
      false,
      'setBarChartDisplay'
    ),
  setMetricFormattingDisplay: (display: MetricFormattingDisplay) =>
    set(
      (state) => {
        state.reportingEngineWidgetSlice.metricFormattingDisplay = display;

        // generate visualization
        state.reportingEngineWidgetSlice.visualization = getChartVisualization(
          state.reportingEngineWidgetSlice
        );
      },
      false,
      'setMetricFormattingDisplay'
    ),
  setMetricDecimalsDisplay: (decimals: string) =>
    set(
      (state) => {
        state.reportingEngineWidgetSlice.metricDecimalsDisplay = decimals;

        // generate visualization
        state.reportingEngineWidgetSlice.visualization = getChartVisualization(
          state.reportingEngineWidgetSlice
        );
      },
      false,
      'setMetricDecimalsDisplay'
    ),
  setIsSelectAllBoard: (isSelectAll: boolean) =>
    set(
      (state) => {
        state.reportingEngineWidgetSlice.isSelectAllBoard = isSelectAll;

        if (isSelectAll) {
          state.reportingEngineWidgetSlice.entitySource = ['all'];
        } else {
          // remove 'all' if exist
          state.reportingEngineWidgetSlice.entitySource =
            state.reportingEngineWidgetSlice.entitySource.filter(
              (board) => board !== 'all'
            );
        }
      },
      false,
      'setIsSelectAllBoard'
    ),
  setEntitySourceOperator: (operator: EntitySourceOperator) =>
    set(
      (state) => {
        state.reportingEngineWidgetSlice.entitySourceOperator = operator;
      },
      false,
      'setEntitySourceOperator'
    ),
  setStudioError: (error: string | null) =>
    set(
      (state) => {
        state.reportingEngineWidgetSlice.studioError = error;
      },
      false,
      'setStudioError'
    ),
  setMetricShowThousandSeparator: (isShowThousandSeparator: boolean) =>
    set(
      (state) => {
        state.reportingEngineWidgetSlice.metricShowThousandSeparator =
          isShowThousandSeparator;

        // generate visualization
        state.reportingEngineWidgetSlice.visualization = getChartVisualization(
          state.reportingEngineWidgetSlice
        );
      },
      false,
      'setMetricShowThousandSeparator'
    ),
  setMetricCurrencySymbol: (symbol: string) =>
    set(
      (state) => {
        state.reportingEngineWidgetSlice.metricCurrencySymbol = symbol;

        // generate visualization
        state.reportingEngineWidgetSlice.visualization = getChartVisualization(
          state.reportingEngineWidgetSlice
        );
      },
      false,
      'setMetricCurrencySymbol'
    ),
  setStudioErrorDatatable: (error: string | null) =>
    set(
      (state) => {
        state.reportingEngineWidgetSlice.studioErrorDatatable = error;
      },
      false,
      'setStudioErrorDatatable'
    ),
});
